import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import { useCookies } from "react-cookie";
import { useHistory } from "react-router-dom";
import {
  calculateLotoThudoReward,
  getLotoThudoResults,
  upsertLotoThudoResult,
} from "../../controller/api/lotoThudoResult";
import { addPeriods, deletePeriod, getLatestPeriodByGame } from "../../controller/api/gamePeriod";
import { Routes } from "../../routes";

const next7Days = new Date();
next7Days.setDate(next7Days.getDate() + 7);

const WEEKDAY_LABEL = {
  0: "Chủ Nhật",
  1: "Thứ 2",
  2: "Thứ 3",
  3: "Thứ 4",
  4: "Thứ 5",
  5: "Thứ 6",
  6: "Thứ 7",
};

function getNextDrawDate(from = new Date()) {
  const date = new Date(from);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateInput(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDateToDisplay(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

function buildResultText(result) {
  return Array.isArray(result) ? result.join(" ") : "";
}

export default function LotoThudoPeriod() {
  const [cookies] = useCookies(["lottmekong"]);
  const history = useHistory();
  const [from, setFrom] = useState(new Date());
  const [to, setTo] = useState(next7Days);
  const [list, setList] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [resultText, setResultText] = useState("");
  const [submittingResult, setSubmittingResult] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDate, setCreateDate] = useState(formatDateInput(getNextDrawDate()));
  const [createQuantity, setCreateQuantity] = useState(1);
  const [loadingSuggestedName, setLoadingSuggestedName] = useState(false);
  const [processingPeriodId, setProcessingPeriodId] = useState("");
  const [deletingPeriodId, setDeletingPeriodId] = useState("");
  const [rewardModal, setRewardModal] = useState({
    show: false,
    gamePeriodId: "",
    periodName: "",
    status: "idle",
    message: "",
    summary: null,
  });

  const suggestedPeriodName = useMemo(() => {
    const names = list
      .map((item) => parseInt(item?.gamePeriod?.name || item?.name || "0", 10))
      .filter((value) => !Number.isNaN(value));

    if (!names.length) {
      return "00001";
    }

    return `${Math.max(...names) + 1}`.padStart(5, "0");
  }, [list]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getLotoThudoResults(
        cookies.lottmekong,
        { from: formatDateInput(from), to: formatDateInput(to) },
        0,
      );
      setList(response.data || []);
    } finally {
      setLoading(false);
      setIsLoaded(true);
    }
  }, [cookies.lottmekong, from, to]);

  const loadSuggestedPeriodName = useCallback(async () => {
    setLoadingSuggestedName(true);
    try {
      const response = await getLatestPeriodByGame(cookies.lottmekong, "loto-thudo");
      const latestName = response?.data?.name;
      const parsedLatestName = parseInt(latestName || "0", 10);

      if (!Number.isNaN(parsedLatestName) && parsedLatestName > 0) {
        setCreateName(`${parsedLatestName + 1}`.padStart(5, "0"));
        return;
      }

      setCreateName(suggestedPeriodName);
    } catch (error) {
      setCreateName(suggestedPeriodName);
    } finally {
      setLoadingSuggestedName(false);
    }
  }, [cookies.lottmekong, suggestedPeriodName]);

  useEffect(() => {
    document.title = "Lô tô điện toán Miền Bắc";
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (showCreateModal) {
      loadSuggestedPeriodName();
    }
  }, [showCreateModal, loadSuggestedPeriodName]);

  const closeResultModal = useCallback(() => {
    if (submittingResult) {
      return;
    }

    setShowModal(false);
    setEditingItem(null);
    setResultText("");
  }, [submittingResult]);

  const closeRewardModal = useCallback(() => {
    if (rewardModal.status === "running") {
      return;
    }

    setRewardModal({
      show: false,
      gamePeriodId: "",
      periodName: "",
      status: "idle",
      message: "",
      summary: null,
    });
  }, [rewardModal.status]);

  const goToStatistic = useCallback((gamePeriodId) => {
    history.push(`${Routes.LotoThudoStatisticByTicket.path}?gamePeriodId=\"${gamePeriodId}\"`);
  }, [history]);

  const openModal = (item) => {
    setEditingItem(item);
    setResultText(buildResultText(item?.result));
    setShowModal(true);
  };

  const submitResult = async () => {
    if (!editingItem) return;

    setSubmittingResult(true);
    try {
      await upsertLotoThudoResult(cookies.lottmekong, {
        gamePeriodId: editingItem.gamePeriodId,
        result: resultText,
      });
      closeResultModal();
      await loadData();
    } finally {
      setSubmittingResult(false);
    }
  };

  const calculateReward = async (item) => {
    const periodId = item.gamePeriodId;
    const periodName = item.gamePeriod?.name || periodId;

    setProcessingPeriodId(periodId);
    setRewardModal({
      show: true,
      gamePeriodId: periodId,
      periodName,
      status: "running",
      message: "Hệ thống đang tính thưởng và cập nhật trạng thái vé.",
      summary: null,
    });

    try {
      const response = await calculateLotoThudoReward(cookies.lottmekong, {
        gamePeriodId: periodId,
      });

      if (response?.status !== "success") {
        setRewardModal({
          show: true,
          gamePeriodId: periodId,
          periodName,
          status: "error",
          message: response?.message || "Không thể tính thưởng. Vui lòng thử lại.",
          summary: null,
        });
        return;
      }

      setRewardModal({
        show: true,
        gamePeriodId: periodId,
        periodName,
        status: "success",
        message: "Đã tính thưởng xong cho kỳ quay này.",
        summary: {
          totalTicket: Number(response?.totalTicket || 0),
          totalRewardTicket: Number(response?.totalRewardTicket || 0),
          totalUnrewardTicket: Number(response?.totalUnrewardTicket || 0),
        },
      });

      await loadData();
    } catch (error) {
      setRewardModal({
        show: true,
        gamePeriodId: periodId,
        periodName,
        status: "error",
        message: "Tính thưởng thất bại. Vui lòng thử lại.",
        summary: null,
      });
    } finally {
      setProcessingPeriodId("");
    }
  };

  const handleDeletePeriod = async (item) => {
    const periodId = item.gamePeriodId;
    const periodName = item.gamePeriod?.name || periodId;
    const hasTickets = Number(item.totalTickets || 0) > 0;

    if (hasTickets) {
      alert("Không thể xoá kỳ đã có vé.");
      return;
    }

    const ok = window.confirm(`Bạn chắc chắn muốn xoá kỳ ${periodName}?`);
    if (!ok) return;

    setDeletingPeriodId(periodId);
    try {
      const response = await deletePeriod(cookies.lottmekong, periodId);
      if (response?.status === "success") {
        await loadData();
      } else {
        alert(response?.message || "Không thể xoá kỳ quay.");
      }
    } finally {
      setDeletingPeriodId("");
    }
  };

  const submitCreatePeriod = async () => {
    if (!createName || !createDate) {
      alert("Vui lòng nhập tên kỳ và ngày quay");
      return;
    }

    const picked = new Date(createDate);
    if (Number.isNaN(picked.getTime())) {
      alert("Vui lòng chọn ngày quay hợp lệ.");
      return;
    }

    setCreateLoading(true);
    try {
      await addPeriods(
        cookies.lottmekong,
        `${parseInt(createName, 10)}`,
        "loto-thudo",
        formatDateToDisplay(createDate),
        "16:15",
        Math.max(parseInt(createQuantity, 10) || 1, 1),
      );
      setShowCreateModal(false);
      setCreateName("");
      await loadData();
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <Card border="light" className="shadow-sm mb-4">
        <Card.Body className="d-flex gap-3 align-items-end flex-wrap">
          <Form.Group>
            <Form.Label>Từ ngày</Form.Label>
            <Form.Control
              type="date"
              value={formatDateInput(from)}
              onChange={(e) => setFrom(new Date(e.target.value))}
              disabled={loading}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Đến ngày</Form.Label>
            <Form.Control
              type="date"
              value={formatDateInput(to)}
              onChange={(e) => setTo(new Date(e.target.value))}
              disabled={loading}
            />
          </Form.Group>
          <Button onClick={loadData} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Đang tải
              </>
            ) : (
              "Lọc"
            )}
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            disabled={loading || processingPeriodId !== ""}
          >
            Tạo kỳ quay
          </Button>
        </Card.Body>
      </Card>

      <div className="position-relative">
        {!isLoaded ? (
          <Card border="light" className="shadow-sm">
            <Card.Body className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </Card.Body>
          </Card>
        ) : (
          <>
            <Card border="light" className="shadow-sm">
              <Card.Body>
                <Table responsive className="align-items-center table-flush">
                  <thead>
                    <tr>
                      <th>Kỳ quay</th>
                      <th>Ngày quay</th>
                      <th>Kết quả</th>
                      <th>Vé / Trúng</th>
                      <th>Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item) => {
                      const itemProcessing = processingPeriodId === item.gamePeriodId;
                      const itemDeleting = deletingPeriodId === item.gamePeriodId;
                      const hasTickets = Number(item.totalTickets || 0) > 0;

                      return (
                        <tr key={item.gamePeriodId}>
                          <td>{item.gamePeriod?.name || item.gamePeriodId}</td>
                          <td>{item.gamePeriod?.resultDate}</td>
          <td>{Array.isArray(item.result) && item.result.length ? item.result.join(" - ") : "Chưa nhập kết quả"}</td>
                          <td>
                            {item.totalTickets || 0} / {item.totalRewardedTickets || 0}
                          </td>
                          <td>
                            <div className="d-flex gap-2 align-items-center">
                              <Button
                                size="sm"
                                variant="info"
                                onClick={() => openModal(item)}
                                disabled={loading || itemProcessing || processingPeriodId !== ""}
                              >
                                Nhập kết quả
                              </Button>
                              {hasTickets ? (
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => calculateReward(item)}
                                  disabled={loading || itemProcessing || processingPeriodId !== ""}
                                >
                                  {itemProcessing ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Đang tính
                                    </>
                                  ) : (
                                    "Tính thưởng"
                                  )}
                                </Button>
                              ) : (
                                <span className="text-muted small fst-italic">
                                  Không có vé để tính thưởng
                                </span>
                              )}
                              {(Number(item.totalRewardedTickets || 0) + Number(item.totalUnRewardedTickets || 0)) > 0 && (
                                <Button
                                  size="sm"
                                  variant="warning"
                                  onClick={() => goToStatistic(item.gamePeriodId)}
                                  disabled={loading || itemProcessing || processingPeriodId !== ""}
                                >
                                  Xem chi tiết
                                </Button>
                              )}
                              {!hasTickets && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => handleDeletePeriod(item)}
                                  disabled={
                                    loading ||
                                    itemProcessing ||
                                    processingPeriodId !== "" ||
                                    deletingPeriodId !== ""
                                  }
                                >
                                  {itemDeleting ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Đang xoá
                                    </>
                                  ) : (
                                    "Xoá kỳ"
                                  )}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!list.length && (
                      <tr>
                        <td colSpan={5} className="text-center py-5 text-muted">
                          Không có dữ liệu
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                style={{
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(1px)",
                }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
          </>
        )}
      </div>

      <Modal show={showModal} onHide={closeResultModal} centered backdrop="static">
        <Modal.Header closeButton={!submittingResult}>
          <Modal.Title>Nhập kết quả điện toán Thủ Đô</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>6 số quay thưởng (01-36)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={resultText}
              onChange={(e) => setResultText(e.target.value)}
              placeholder="Nhập đúng 6 số, ví dụ: 03 12 18 25 31 36"
              disabled={submittingResult}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeResultModal} disabled={submittingResult}>
            Đóng
          </Button>
          <Button variant="primary" onClick={submitResult} disabled={submittingResult}>
            {submittingResult ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Đang lưu
              </>
            ) : (
              "Lưu"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateModal} onHide={() => !createLoading && setShowCreateModal(false)} centered backdrop="static">
        <Modal.Header closeButton={!createLoading}>
          <Modal.Title>Tạo kỳ quay điện toán Thủ Đô</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Tên kỳ</Form.Label>
            <Form.Control
              type="number"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="Ví dụ: 00015"
              disabled={loadingSuggestedName || createLoading}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Ngày quay đầu tiên</Form.Label>
            <Form.Control
              type="date"
              value={createDate}
              onChange={(e) => setCreateDate(e.target.value)}
              disabled={createLoading}
            />
            <Form.Text className="text-muted">
              Lô tô điện toán Thủ Đô đối chiếu 6 số quay thưởng điện toán 6/36.
              {createDate && !Number.isNaN(new Date(createDate).getTime()) && (
                <> Ngày đã chọn: <strong>{WEEKDAY_LABEL[new Date(createDate).getDay()]}</strong>.</>
              )}
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>Số lượng kỳ</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={createQuantity}
              onChange={(e) => setCreateQuantity(e.target.value)}
              disabled={createLoading}
            />
            <Form.Text className="text-muted">
              Hệ thống sẽ tạo liên tiếp từng ngày, giờ chốt mặc định 16:15.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={createLoading}>
            Đóng
          </Button>
          <Button variant="primary" onClick={submitCreatePeriod} disabled={createLoading}>
            {createLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Đang tạo
              </>
            ) : (
              "Tạo kỳ"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={rewardModal.show} onHide={closeRewardModal} centered backdrop="static">
        <Modal.Header closeButton={rewardModal.status !== "running"}>
          <Modal.Title>Tính thưởng kỳ #{rewardModal.periodName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center mb-3">
            {rewardModal.status === "running" ? (
              <Spinner animation="border" variant="primary" className="me-3" />
            ) : (
              <div
                className="me-3"
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor:
                    rewardModal.status === "success"
                      ? "#2ca58d"
                      : rewardModal.status === "error"
                        ? "#dc3545"
                        : "#d1d5db",
                }}
              />
            )}
            <div>
              <div className="fw-bold">
                {rewardModal.status === "running"
                  ? "Đang tính thưởng"
                  : rewardModal.status === "success"
                    ? "Đã tính thưởng xong"
                    : "Tính thưởng thất bại"}
              </div>
              <div className="text-muted small">{rewardModal.message}</div>
            </div>
          </div>

          {rewardModal.summary && (
            <div className="row g-3">
              <div className="col-4">
                <div className="border rounded p-3 text-center h-100">
                  <div className="text-muted small">Đã xử lý</div>
                  <div className="fw-bold fs-5">{rewardModal.summary.totalTicket}</div>
                </div>
              </div>
              <div className="col-4">
                <div className="border rounded p-3 text-center h-100">
                  <div className="text-muted small">Vé trúng</div>
                  <div className="fw-bold fs-5 text-success">{rewardModal.summary.totalRewardTicket}</div>
                </div>
              </div>
              <div className="col-4">
                <div className="border rounded p-3 text-center h-100">
                  <div className="text-muted small">Không trúng</div>
                  <div className="fw-bold fs-5 text-danger">{rewardModal.summary.totalUnrewardTicket}</div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {rewardModal.status === "success" && processingPeriodId === "" && (
            <Button
              variant="warning"
              onClick={() => {
                closeRewardModal();
                if (rewardModal.summary && rewardModal.gamePeriodId) {
                  goToStatistic(rewardModal.gamePeriodId);
                }
              }}
            >
              Xem vé đã tính
            </Button>
          )}
          <Button
            variant={rewardModal.status === "success" ? "success" : "secondary"}
            onClick={closeRewardModal}
            disabled={rewardModal.status === "running"}
          >
            {rewardModal.status === "running" ? "Đang xử lý" : "Đóng"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
